import React, { useRef, useState, useEffect, useMemo } from 'react';
import Image from 'next/image';
import Lottie from 'lottie-react';
import { loadingAnimation } from './Loading-Animation';
import { createPortal } from 'react-dom';
import { useSpring, animated } from '@react-spring/web';

import { CFColorTokenProp } from '../../../theme/tokens/contentful-color';

import { WidgetProps, DEFAULT_BUTTON_TEXT, SharedProps } from '../shared';
import cx from 'classnames';

import errorIcon from '@/images/ic_error_circle_filled.svg';
import RichText from '~/components/RichText';
import { RenderNode } from '@contentful/rich-text-react-renderer';
import { Block, BLOCKS, Inline } from '@contentful/rich-text-types';
import { formatToPhone } from '~/utils/formatPhoneNumber';

const CF_COLORS: Record<string, string> = {
  spearmint: '#80D6B0',
  green: '#3BBFAD',
  ginger: '#F4BA42',
  blackberry: '#1D252C',
  cinnamint: '#D66755',
  sweetmint: '#035C67',
  white: '#FFFFFF',
  mutedNavy: '#9AB5B8',
  transparent: 'transparent',
  limegreen: '#BBF174',
  callout: '#CDE012'
};

const getTokenKey = (token?: CFColorTokenProp): string | undefined => {
  if (!token) return undefined;
  if (typeof token === 'string') return token;
  const obj = token as Record<string, string>;
  return obj['0'] || obj.min || obj.mobile || obj.tablet || obj.desktop || obj.max;
};

const resolveColor = (token?: CFColorTokenProp, fallback = '#CDE012'): string => {
  const key = getTokenKey(token);
  return key ? CF_COLORS[key] || fallback : fallback;
};

const getTextColor = (token?: CFColorTokenProp): string => {
  const key = getTokenKey(token);
  return key && ['blackberry', 'sweetmint'].includes(key) ? '#FFFFFF' : '#1D252C';
};

export interface GetStartedWidgetProps extends WidgetProps {
  onStartClick?: () => void;
}

type LoaderStickyButtonProps = WidgetProps & SharedProps;

const ctaOverrideOptions: RenderNode = {
  [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children: React.ReactNode) => (
    <p className="z-1 top-full w-full px-2 text-body-caption_web-desktop-max font-normal leading-[14px] text-on-primary-surface">
      {React.Children.map(children, (child, idx) =>
        // Add a space before any link except the first child
        idx > 0 && React.isValidElement(child) && child.type === 'a'
          ? [<span key={`space-${idx}`}> </span>, child]
          : child
      )}
    </p>
  )
};

export function LoaderStickyButton({
  autocompleteAttribute,
  buttonColor = 'callout' as CFColorTokenProp,
  buttonHoverColor = 'green',
  buttonText = DEFAULT_BUTTON_TEXT,
  buttonContent,
  privacyDisclaimerDropdown,
  isScrolled = false,
  label,
  errorMessage,
  showErrorBanner,
  inputProps,
  submitted
}: LoaderStickyButtonProps): React.ReactElement | null {
  const [isStartClicked, setIsStartClicked] = useState(false);
  const [showAnimation, setShowAnimation] = useState(false);
  const [inputClicked, setInputClicked] = useState(false);
  const [mounted, setMounted] = useState(false);

  const getStartInputRef = useRef<HTMLInputElement | null>(null);
  const tabIndex = isStartClicked ? 0 : -1;
  buttonColor = buttonColor || 'callout';

  const baseBgColor = useMemo(
    () => resolveColor(buttonColor as CFColorTokenProp),
    [buttonColor]
  );
  const hoverBgColor = useMemo(
    () => resolveColor(buttonHoverColor as CFColorTokenProp, '#3BBFAD'),
    [buttonHoverColor]
  );
  const buttonTextColor = useMemo(
    () => getTextColor(buttonColor as CFColorTokenProp),
    [buttonColor]
  );
  const [inputButtonHovered, setInputButtonHovered] = useState(false);
  const [mainButtonHovered, setMainButtonHovered] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    if (submitted && !showAnimation) {
      setShowAnimation(true);
    }
  }, [submitted, showAnimation]);

  const handleButtonClick = (): void => {
    setIsStartClicked(true);
    getStartInputRef.current?.focus();
  };

  useEffect(() => {
    function handleDocumentClick(e: MouseEvent) {
      const el = getStartInputRef.current;
      if (el && !el.contains(e.target as Node)) {
        setInputClicked(false);
      }
    }
    if (isStartClicked) {
      document.addEventListener('mousedown', handleDocumentClick);
    }
    if (errorMessage && isStartClicked && getStartInputRef.current) {
      getStartInputRef.current?.focus();
    }
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [errorMessage, isStartClicked]);

  // Animation for input/button transition
  const flexWrapperSpring = useSpring({
    to: {
      opacity: 1,
      transform: isStartClicked ? 'translateY(0%)' : 'translateY(100%)'
    },
    config: { tension: 522, friction: 34, mass: 1, duration: 150 }
  });

  // Animation for the initial button (slide out when isStartClicked)
  const buttonSpring = useSpring({
    to: {
      transform: isStartClicked ? 'translateY(100%)' : 'translateY(0%)',
      opacity: isStartClicked ? 0 : 1
    },
    config: { tension: 522, friction: 34, mass: 1, duration: 150 }
  });

  return (
    <>
      {mounted && showAnimation && (
        <>
          {createPortal(
            <div
              data-testid="loader-sticky-button-overlay"
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100%',
                height: '100%',
                backgroundColor: '#00413A',
                zIndex: 999999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'auto'
              }}
            >
              <Lottie
                animationData={loadingAnimation}
                loop={true}
                style={{
                  width: '100%',
                  height: '100%',
                  backgroundColor: '#00413A'
                }}
              />
            </div>,
            document.body
          )}
        </>
      )}
      <div
        className={cx('relative m-0 w-full max-w-none p-0', {
          'h-[78px]': isStartClicked,
          'h-[105px]': errorMessage
        })}
        style={{
          position: 'relative',
          background: 'transparent'
        }}
      >
        <animated.div
          data-testid="loader-sticky"
          id="flex-wrapper"
          className={cx(
            'fixed bottom-0 left-0 flex w-screen items-end justify-center'
          )}
          style={{
            background: '#F2F5F5',
            zIndex: 1001,
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            width: '100vw',
            WebkitBackfaceVisibility: 'hidden',
            WebkitOverflowScrolling: 'touch',
            WebkitPerspective: '1000',
            backfaceVisibility: 'hidden',
            perspective: '1000',
            ...flexWrapperSpring
          }}
        >
          <div className="relative box-border flex w-full flex-col justify-center self-center px-4 pb-8 pt-4">
            <div className="z-[1] flex h-full w-full items-center">
              <div className="relative m-0 w-full max-w-none p-0">
                <div className="relative flex w-full">
                  <input
                    {...inputProps}
                    data-testid="mobile-phone-number-input"
                    id="mobile-phone-number-input"
                    name="mobile-phone-number-input"
                    ref={getStartInputRef}
                    tabIndex={tabIndex}
                    placeholder=""
                    autoComplete={autocompleteAttribute ?? 'tel'}
                    aria-invalid={!!errorMessage}
                    aria-describedby={
                      errorMessage ? 'cta-error-message' : undefined
                    }
                    type="tel"
                    className={cx(
                      'block h-[56px] w-full min-w-0 max-w-full flex-1 rounded-[8px] border bg-white px-4 py-3 pr-[120px] text-body-2_web-desktop-max font-normal outline-none transition-all duration-200 placeholder:text-[16px] placeholder:font-normal',
                      inputClicked || inputProps?.value ? 'pt-6' : '',
                      inputProps?.value || inputClicked
                        ? 'border-[#4EE0A4]'
                        : 'border-[#3436361A]'
                    )}
                    style={{
                      width: '100%',
                      fontSize: '16px',
                      WebkitTransform: 'translate3d(0, 0, 0)',
                      WebkitBackfaceVisibility: 'hidden',
                      WebkitOverflowScrolling: 'touch',
                      WebkitPerspective: '1000',
                      transform: 'translate3d(0, 0, 0)',
                      backfaceVisibility: 'hidden',
                      perspective: '1000',
                      ...inputProps?.style
                    }}
                    onClick={() => setInputClicked(true)}
                    onBlur={() => setInputClicked(false)}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={14}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      const allowedKeys = [
                        'Backspace',
                        'Delete',
                        'ArrowLeft',
                        'ArrowRight',
                        'Tab',
                        'Home',
                        'End'
                      ];
                      const isModifier = e.ctrlKey || e.metaKey || e.altKey;
                      const isDigit = e.key >= '0' && e.key <= '9';
                      if (
                        !isDigit &&
                        !allowedKeys.includes(e.key) &&
                        !isModifier
                      ) {
                        e.preventDefault();
                      }
                    }}
                    value={inputProps?.value}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const raw = e?.target?.value ?? '';
                      const digitsOnly = raw.replace(/\D/g, '');
                      const formatted = formatToPhone(digitsOnly) || '';
                      inputProps?.handleOnChange?.(formatted);
                    }}
                  />

                  <label
                    data-testid="mobile-phone-number-label"
                    htmlFor="mobile-phone-number-input"
                    className={cx(
                      'pointer-events-none absolute left-4 font-normal text-on-primary-surface transition-all duration-200',
                      {
                        'top-2 text-[12px] font-medium text-on-primary-surface':
                          inputClicked || inputProps?.value,
                        'top-1/2 -translate-y-1/2': !(
                          inputClicked || inputProps?.value
                        )
                      }
                    )}
                  >
                    {label}
                  </label>
                  {inputProps?.value && (
                    <button
                      type="submit"
                      aria-label="Get started"
                      data-testid="get-started-button"
                      className="absolute right-0 top-1/2 flex h-10 -translate-y-1/2 items-center justify-center rounded-[8px] whitespace-nowrap px-3"
                      style={{
                        margin: isScrolled ? 'auto 2%' : 'auto 8px',
                        backgroundColor: inputButtonHovered ? hoverBgColor : baseBgColor,
                        color: buttonTextColor,
                        transition: 'background-color 0.2s ease'
                      }}
                      onMouseEnter={() => setInputButtonHovered(true)}
                      onMouseLeave={() => setInputButtonHovered(false)}
                      onFocus={() => setInputButtonHovered(true)}
                      onBlur={() => setInputButtonHovered(false)}
                    >
                      <span className="text-sm font-medium">
                        {buttonContent || buttonText}
                      </span>
                    </button>
                  )}
                </div>
              </div>
            </div>
            {privacyDisclaimerDropdown && (
              <div
                data-testid="privacy-disclaimer"
                className="z-1 top-full mt-2 flex w-full flex-row flex-wrap items-center"
                style={{
                  WebkitTransform: 'translate3d(0, 0, 0)',
                  WebkitBackfaceVisibility: 'hidden',
                  WebkitOverflowScrolling: 'touch',
                  WebkitPerspective: '1000',
                  transform: 'translate3d(0, 0, 0)',
                  backfaceVisibility: 'hidden',
                  perspective: '1000'
                }}
              >
                <RichText
                  data={privacyDisclaimerDropdown}
                  overrideOptions={ctaOverrideOptions}
                />
              </div>
            )}
            {showErrorBanner && errorMessage && (
              <div
                data-testid="error-message"
                className={cx(
                  `z-1 top-full mt-2 flex w-full items-center px-2 `
                )}
                style={{
                  WebkitTransform: 'translate3d(0, 0, 0)',
                  WebkitBackfaceVisibility: 'hidden',
                  WebkitOverflowScrolling: 'touch',
                  WebkitPerspective: '1000',
                  transform: 'translate3d(0, 0, 0)',
                  backfaceVisibility: 'hidden',
                  perspective: '1000'
                }}
              >
                <Image src={errorIcon} alt="Error icon" />
                <p
                  id="cta-error-message"
                  className="ml-2 text-body-3_web-desktop-max text-negative-strong"
                >
                  {errorMessage}
                </p>
              </div>
            )}
          </div>
        </animated.div>
      </div>
      <animated.div
        className={cx(
          'fixed bottom-0 left-0 flex w-screen items-end justify-center'
        )}
        style={{
          background: '#F2F5F5',
          zIndex: 1001,
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          width: '100vw',
          WebkitBackfaceVisibility: 'hidden',
          WebkitOverflowScrolling: 'touch',
          WebkitPerspective: '1000',
          backfaceVisibility: 'hidden',
          perspective: '1000',
          ...buttonSpring
        }}
      >
        <div className="relative box-border flex w-full flex-col justify-center self-center px-4">
          <div className="w-full pb-8 pt-4">
            <button
              type="button"
              onClick={handleButtonClick}
              aria-label="Get started"
              data-testid={!isStartClicked ? 'get-started-button' : undefined}
              className={cx(
                'relative z-[1] h-[56px] min-h-[32px] w-full max-w-none cursor-pointer self-center overflow-hidden whitespace-nowrap rounded-[8px] border-0 text-[16px] font-medium outline-0 items-center justify-center transition-colors duration-200'
              )}
              style={{
                width: '100%',
                backgroundColor: mainButtonHovered ? hoverBgColor : baseBgColor,
                color: buttonTextColor,
                WebkitTransform: 'translate3d(0, 0, 0)',
                WebkitBackfaceVisibility: 'hidden',
                WebkitOverflowScrolling: 'touch',
                WebkitPerspective: '1000',
                transform: 'translate3d(0, 0, 0)',
                backfaceVisibility: 'hidden',
                perspective: '1000'
              }}
              onMouseEnter={() => setMainButtonHovered(true)}
              onMouseLeave={() => setMainButtonHovered(false)}
              onFocus={() => setMainButtonHovered(true)}
              onBlur={() => setMainButtonHovered(false)}
            >
              {buttonContent ?? buttonText}
            </button>
          </div>
        </div>
      </animated.div>
    </>
  );
}
